import { Select } from "antd";
import { useLocalStorage } from "react-use";
import { CalendarOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { Fade } from "react-awesome-reveal";

import isEmpty from "lodash/isEmpty";
import isFinite from "lodash/isFinite";
import get from "lodash/get";
import sumBy from "lodash/sumBy";
import moment from "moment";

import { styles, viewTypes } from "@evolved/constants";
import { opportunityFields } from "@evolved/views";

import { Filters } from "./filters";
import { EmptyBox } from "components/image-placeholders";
import { WidgetHeader } from "../widget-header";
import { WidgetContainer } from "../widget-container";
import { formatDollars } from "utils/format-dollars";
import { withSalesRole } from "../../../components/with-role";

import { useOpportunities } from "../../../data/use-opportunities";
import { useOpportunityStates } from "../../../data/use-opportunity-states";
import { useOrganization } from "../../../data/use-organization";
import { useViewStore } from "../../../stores/view";

const { CLICKABLE_STYLE } = styles;
const { OPPORTUNITY } = viewTypes;

export const useOpportunityForecastState = (organization) => {
  const opportunityStates = useOpportunityStates();

  const potentialOpenStates = opportunityStates
    .all({ includeArchived: false })
    .filter(({ systemKey }) => systemKey !== "WON" && systemKey !== "LOST");

  // NOTE: these are just called legacyOpenStates because they may be a string.
  // Ensure they still exist and convert to a number.
  const [legacyOpenStates, setOpenStates] = useLocalStorage(
    `${organization._id}.LAYOUT_STATE.DASHBOARD.OPPORTUNITY_FORECAST_OPEN_STATES`,
    potentialOpenStates.map(({ _id }) => _id)
  );

  const [rangeFilter, setRangeFilter] = useLocalStorage(
    `${organization._id}.LAYOUT_STATE.DASHBOARD.OPPORTUNITY_FORECAST_RANGE_FILTER`
  );

  const [filterKey, setFilterKey] = useLocalStorage(
    `${organization._id}.LAYOUT_STATE.DASHBOARD.OPPORTUNITY_FORECAST_FILTER_KEY`,
    "targetWinDate"
  );

  return {
    filterKey,
    legacyOpenStates,
    rangeFilter,
    setFilterKey,
    setOpenStates,
    setRangeFilter,
  }
};

export const filterOption = (input, option) =>
  (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

const OpportunitiesLink = () => {
  const navigate = useNavigate();

  return (
    <span onClick={() => navigate("/opportunities")} style={CLICKABLE_STYLE}>
      opportunities
    </span>
  );
};

const Component = (props) => {
  const {
    filterKey,
    legacyOpenStates,
    rangeFilter,
    setFilterKey,
    setOpenStates,
    setRangeFilter,
  } = props;

  const navigate = useNavigate();

  const viewOpportunities = (dateRange, opportunityStates) => {
    useViewStore.getState().setView(OPPORTUNITY)({
      fields: useViewStore.getState()[OPPORTUNITY].fields,
      filters: [
        {
          dataIndex: opportunityFields.targetWinDate.dataIndex,
          value: dateRange,
        },
        {
          dataIndex: opportunityFields.state.dataIndex,
          value: opportunityStates,
        },
      ],
      preset: true,
    });

    navigate("/opportunities");
  };

  const organization = useOrganization();
  const opportunityStates = useOpportunityStates();
  const opportunities = useOpportunities().all({ includeArchived: false });

  const customDateFields = organization.userDefinedFields.filter(
    ({ dataType, type }) => dataType === "DATE" && type === "OPPORTUNITY"
  );

  const potentialOpenStates = opportunityStates
    .all({ includeArchived: false })
    .filter(({ systemKey }) => systemKey !== "WON" && systemKey !== "LOST");

  const openStates = legacyOpenStates
    .filter((id) => opportunityStates.byId(id, { includeArchived: false }))
    .map((id) => Number(id));

  let rangeFilterLabel = "";

  if (rangeFilter) {
    const [, year, month] = rangeFilter.split("_");

    rangeFilterLabel = `${month}, ${year}`;
  }

  const allStates = opportunityStates
    .all()
    .filter(
      ({ _id, systemKey }) => openStates.includes(_id) || systemKey === "WON"
    );

  const inProgressStates = allStates.filter(
    ({ systemKey }) => systemKey !== "WON"
  );
  const wonStates = allStates.filter(({ systemKey }) => systemKey === "WON");

  const [, year, month] = rangeFilter?.split("_") || [];

  // NOTE: only consider opportunities during this period that HAVE:
  // - been targeted to win in it
  // - NOT been won yet
  // - or IF won, was won IN the period of time
  // i.e. an opportunity targeted for December, but won in October, should not show up 
  // in the numbers at all. (you can't select period of time in the past anyway)
  const filteredOpportunities = rangeFilter
    ? opportunities.filter((opportunity) => {
      // NOTE: target win date, for example
      const value = get(opportunity, filterKey);

      if (!allStates.map(({ _id }) => _id).includes(opportunity.stateId)) {
        return false;
      }

      // NOTE: Only consider opportunities that were forecasted to be won in
      // this period.
      if (
        !isFinite(value) ||
        !moment
          .unix(value)
          .isBetween(
            moment().year(year).month(month).startOf("month"),
            moment().year(year).month(month).endOf("month")
          )
      ) {
        return false;
      }

      // NOTE: If won, only consider if won in this period.
      // If won previously, we don't consider it, it's already won. 
      if (wonStates.map(({ _id }) => _id).includes(opportunity.stateId)) {
        return moment
          .unix(opportunity.lastStateChangeOn)
          .isBetween(
            moment().year(year).month(month).startOf("month"),
            moment().year(year).month(month).endOf("month")
          );
      }

      return true;
    })
    : [];

  const alreadyWon = filteredOpportunities.filter(
    (opportunity) =>
      opportunityStates.byId(opportunity.stateId)?.systemKey === "WON"
  );

  const inProgress = filteredOpportunities.filter(
    (opportunity) =>
      opportunityStates.byId(opportunity.stateId)?.systemKey !== "WON"
  );

  if (isEmpty(opportunities)) {
    return (
      <Fade>
        <div>
          <div
            style={{
              alignItems: "center",
              display: "flex",
              marginBottom: "12px",
            }}
          >
            <WidgetHeader label="Opportunity Value Forecast" />
          </div>
          <EmptyBox
            primary={"You do not yet have any opportunities."}
            secondary={
              <>
                Head over to your <OpportunitiesLink /> and log some
              </>
            }
          />
        </div>
      </Fade>
    );
  }

  let body;

  if (!rangeFilter) {
    body = (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          fontSize: "16px",
          fontWeight: 600,
          marginBottom: "24px",
        }}
      >
        Select a date range filter to see your opportunity forecast.{" "}
        <span style={{ fontSize: "24px", marginRight: "8px" }}>🚀</span>
      </div>
    );
  } else {
    body = (
      <div
        style={{
          alignItems: "center",
          display: "flex",
          marginBottom: "24px",
          justifyContent: "space-between",
        }}
      >
        <div
          onClick={() =>
            viewOpportunities(
              rangeFilter,
              allStates.map(({ _id }) => _id)
            )
          }
          style={{
            cursor: "pointer",
          }}
        >
          <div
            style={{
              fontSize: "14px",
              fontWeight: "600",
              color: "black",
            }}
          >
            Total
          </div>
          <div
            style={{
              fontSize: "32px",
              fontWeight: "600",
              color: "#7F5196",
              lineHeight: "40px",
            }}
          >
            <span
              style={{
                fontSize: "18px",
                lineHeight: "32px",
              }}
            >
              $
            </span>
            {formatDollars(sumBy(filteredOpportunities, "value"), {
              noSign: true,
            })}
          </div>
          <div
            style={{
              fontSize: "16px",
              fontWeight: "600",
              lineHeight: 1,
              color: "#8C8C8C",
            }}
          >
            From {filteredOpportunities.length}
          </div>
        </div>
        {alreadyWon.length > 0 && (
          <>
            <div
              onClick={() =>
                viewOpportunities(
                  rangeFilter,
                  wonStates.map(({ _id }) => _id)
                )
              }
              style={{
                cursor: "pointer",
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "black",
                }}
              >
                Already Won
              </div>
              <div
                style={{
                  fontSize: "32px",
                  fontWeight: "600",
                  color: "#7F5196",
                  lineHeight: "40px",
                }}
              >
                <span
                  style={{
                    fontSize: "18px",
                    lineHeight: "32px",
                  }}
                >
                  $
                </span>
                {formatDollars(sumBy(alreadyWon, "value"), { noSign: true })}
              </div>
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  lineHeight: 1,
                  color: "#8C8C8C",
                }}
              >
                From {alreadyWon.length}
              </div>
            </div>

            <div
              onClick={() =>
                viewOpportunities(
                  rangeFilter,
                  inProgressStates.map(({ _id }) => _id)
                )
              }
              style={{
                cursor: "pointer",
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "black",
                }}
              >
                In Progress
              </div>
              <div
                style={{
                  fontSize: "32px",
                  fontWeight: "600",
                  color: "#7F5196",
                  lineHeight: "40px",
                }}
              >
                <span
                  style={{
                    fontSize: "18px",
                    lineHeight: "32px",
                  }}
                >
                  $
                </span>
                {formatDollars(sumBy(inProgress, "value"), { noSign: true })}
              </div>
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  lineHeight: 1,
                  color: "#8C8C8C",
                }}
              >
                From {inProgress.length}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <Fade>
      <div>
        <div
          style={{
            alignItems: "center",
            display: "flex",
            marginBottom: "12px",
          }}
        >
          <WidgetHeader label="Opportunity Value Forecast" />
          <h4
            style={{
              marginBottom: "0px",
              marginRight: "8px",
              fontSize: "18px",
            }}
          >
            {rangeFilterLabel}
          </h4>
          <CalendarOutlined style={{ fontSize: "18px" }} />
        </div>
        <div style={{ display: "flex", marginBottom: "12px" }}>
          <div style={{ fontWeight: 600, width: "75px", marginRight: "8px" }}>
            At Stage
          </div>
          <div style={{ flexGrow: 1 }}>
            <Select
              filterOption={filterOption}
              mode="multiple"
              notFoundContent={"No match"}
              optionFilterProp="children"
              showSearch
              options={potentialOpenStates.map((option) => ({
                value: option._id,
                label: option.label,
              }))}
              value={openStates}
              onChange={setOpenStates}
              size="small"
              style={{ width: "100%" }}
            />
            <div style={{ marginLeft: "4px" }}>*Won is always included</div>
          </div>
        </div>
        <Filters
          customDateFields={customDateFields}
          setFilterKey={setFilterKey}
          filterKey={filterKey}
          setRangeFilter={setRangeFilter}
          rangeFilter={rangeFilter}
        />
        {body}
      </div>
    </Fade>
  );
};

const Container = (props) => {
  return (
    <WidgetContainer>
      <Component {...props} />
    </WidgetContainer>
  );
};

export const OpportunityForecast = withSalesRole(Container);
